import axios from '@/libs/axios'
import router from '@/router'
import ability from '@/libs/acl/ability'
import store from '@/store'
import { mixins } from '@/mixins'
import { entityPatient, entityPatientCall } from '@/libs/acl/entities'
import {
  getUserData, removeAccessToken, removeRefreshToken, removeUserData, setAccessToken, setRefreshToken, setUserData,
} from '@/auth/utils'
import moment from 'moment-timezone'
import {
  minimumSmsNotificationBalanceNotify, subscriptionExpiredNotify, systemNotify
} from '@/composables/notificationHelper'
import { addDataToLocalStorageByPrefix } from '@/composables/localStorageHelper'
import appConstants from '@/constants/constants'
import { showCommonToast, showInactiveSessionToast, showNewMessageToast } from '@/composables/toast'
import useSocket from '@/composables/socket'
import useApp from '@/composables/app'
import { broadcastChannel } from '@/broadcast-channel'
import useWindow from '@/composables/window'

const processLogin = (commit, rootGetters, response) => {
  const { refreshLastActionTime } = useWindow()
  commit('info/EMPTY_ABILITIES', null, { root: true })
  const data = response.data
  setAccessToken(data.access)
  setRefreshToken(data.refresh)
  if (data.isRoot) {
    commit('info/SET_ROOT_ABILITIES', null, { root: true })
  } else {
    commit('info/SET_ABILITIES', data.permissions, { root: true })
  }
  const abilities = store.getters['info/abilities']
  ability.update(abilities)
  setUserData({
    id: data.id,
    fullName: data.last_name + ' ' + data.first_name + ' ' + data.middle_name,
    last_name: data.last_name,
    first_name: data.first_name,
    middle_name: data.middle_name,
    phone: data.phone,
    birth_date: data.birth_date,
    avatar: data.avatar ? data.avatar.src : null,
    role: data.role,
    role_type: data.role_type,
    username: data.username,
    ability: abilities,
    isRoot: data.isRoot,
    integrator_id: data.integrator_id,
    integrator_name: data.integrator_name,
    integrator_work_time_from: data.integrator_work_time_from,
    integrator_work_time_to: data.integrator_work_time_to,
    program_version: data.program_version,
    subscription_expired_at: data.subscription_expired_at,
    subscription_month_price: data.subscription_month_price,
    service_calculation_export_enabled: data.service_calculation_export_enabled,
    treatment_presentation_enabled: data.treatment_presentation_enabled,
    patient_contact_view_password_sending_method: data.patient_contact_view_password_sending_method,
    system_modules: data.system_modules,
    logo_svg: data.logo_svg,
    logo_png: data.logo_png,
    users_limit: data.users_limit,
    two_f_a_auth_enabled: data.two_f_a_auth_enabled,
    price_settings: data.price_settings,
    viber_api_enabled: data.viber_api_enabled,
    telegram_api_enabled: data.telegram_api_enabled,
    branch_logic_enabled: data.branch_logic_enabled,
    ate_enabled: data.ate_enabled,
    helix_enabled: data.helix_enabled,
    online_registration_api_available: data.online_registration_api_available,
    has_integrator_branches: data.has_integrator_branches,
    integrator_branch_id: data.integrator_branch_id,
    document_settings: data.document_settings,
    treatment_diary_documents: data.treatment_diary_documents,
    insurance_documents: data.insurance_documents,
    treatment_diary_settings: data.treatment_diary_settings,
    patient_card_settings: data.patient_card_settings,
    treatment_presentation_settings: data.treatment_presentation_settings,
    patient_reception_schedule_settings: data.patient_reception_schedule_settings,
    integrator_type: data.integrator_type,
    employee_settings: data.employee_settings,
    dental_service_settings: data.dental_service_settings,
    cash_register_settings: data.cash_register_settings,
    online_appointment_settings: data.online_appointment_settings,
    promo_payment_settings: data.promo_payment_settings,
    timezone: data.timezone,
    online: data.online,
    sending_sms_to_patient_is_possible: data.sending_sms_to_patient_is_possible,
    sms_notification_balance: data.sms_notification_balance,
    min_sms_notification_balance: data.min_sms_notification_balance,
    sms_notification_point_price: data.sms_notification_point_price
  })
  if (data.timezone) {
    moment.tz.setDefault(data.timezone)
  }
  commit('SET_USER', data)
  if (
    mixins.methods.getUserRoleType() === 'operator' && mixins.methods.isATEEnabled()
      && ability.can('read', entityPatientCall)
  ) {
    addDataToLocalStorageByPrefix(appConstants.user.sessionState.IS_ONLINE_LOCAL_STORAGE_KEY, { online: true })
  }

  refreshLastActionTime()
  redirectAfterLogin(data)
}

const processLogout = (commit, reason) => {
  const { clearUserOnlineChecker } = useWindow()
  const { clearUserSocketConnection } = useSocket()

  if (
    mixins.methods.getUserRoleType() === 'operator' && mixins.methods.isATEEnabled()
      && ability.can('read', entityPatientCall)
  ) {
    addDataToLocalStorageByPrefix(appConstants.user.sessionState.IS_ONLINE_LOCAL_STORAGE_KEY, { online: false })
  }
  clearUserSocketConnection()
  removeUserData()
  clearUserOnlineChecker()

  commit('info/EMPTY_ABILITIES', null, { root: true })
  commit('info/SET_PATIENT_APPOINTMENT_STATUS', [], { root: true })
  commit('info/SET_INTEGRATORS', [], { root: true })
  commit('info/SET_INTEGRATOR', null, { root: true })
  commit('info/SET_THIRD_PARTY_ORGANIZATIONS_FOR_DROPDOWN', [], { root: true })
  commit('info/SET_INSURANCE_COMPANIES_FOR_DROPDOWN', [], { root: true })
  commit('info/SET_RECOMMENDED_VISIT_TYPES_FOR_DROPDOWN', [], { root: true })
  commit('info/SET_PAYMENT_METHODS', [], { root: true })
  commit('employee/SET_DIRECTIONS', [], { root: true })
  commit('employee/SET_DIRECTIONS_FOR_DROPDOWN', [], { root: true })
  commit('employee/SET_DOCTORS', [], { root: true })
  commit('employee/SET_MEDICAL_STAFF_FOR_DROPDOWN', [], { root: true })
  commit('employee/SET_OPERATORS', [], { root: true })
  commit('employee/SET_ROLE_TYPES', [], { root: true })
  commit('employee/SET_ALL_USERS', [], { root: true })
  commit('patient/SET_PATIENTS_FOR_DROPDOWN', [], { root: true })
  commit('schedule/SET_DOCTORS', [], { root: true })
  commit('schedule/SET_DESIGNATIONS', [], { root: true })
  commit('schedule/SET_SELECTED_SIDEBAR_DOCTOR_ID', null, { root: true })
  commit('schedule/SET_SELECTED_SIDEBAR_DOCTOR_IDS', [], { root: true })
  commit('wage/SET_MEDICAL_STAFF', [], { root: true })
  commit('todo/SET_TAGS', [], { root: true })

  removeAccessToken()
  removeRefreshToken()
  delete axios.defaults.headers.common.Authorization
  if ([
    appConstants.auth.logoutReason.INACTIVE_SESSION, appConstants.auth.logoutReason.END_OF_SESSION
  ].includes(reason)) {
    showInactiveSessionToast(reason)
  }
  if (router.currentRoute.name !== 'auth-login') {
    router.push({ name: 'auth-login' })
  }
  broadcastChannel.postMessage({ type: 'LOGOUT', reason })
}

const redirectAfterLogin = data => {
  const systemModules = data.system_modules
  const isRoot = data.isRoot
  const defaultComponentRoute = 'patient-cards'
  const { goToHomePage } = useApp()

  if (isRoot) {
    router
      .push({ name: defaultComponentRoute })
      .then(() => afterRedirect(data))
  } else {
    const index = systemModules.findIndex(sm => sm === defaultComponentRoute)
    if (index > -1) {
      router
        .push({ name: defaultComponentRoute })
        .then(() => afterRedirect(data))
    } else {
      const result = goToHomePage()
      if (result) {
        result.then(() => afterRedirect(data))
      } else {
        afterRedirect(data)
      }
    }
  }
}

const afterRedirect = data => {
  const { connectToUserSocket } = useSocket()
  const { setUserOnlineChecker } = useWindow()

  connectToUserSocket()
  setUserOnlineChecker()
  if (data.unseen_notifications) {
    processSystemNotifications(data.unseen_notifications)
  }
  if (data.subscription_expires_notif) {
    subscriptionExpiredNotify(data.subscription_expires_notif)
  }
  if (data.minimum_sms_notification_balance_notif) {
    minimumSmsNotificationBalanceNotify(data.minimum_sms_notification_balance_notif)
  }
  if (data.unread_chat_messages) {
    processUnreadChatMessages(data.unread_chat_messages)
  }
  broadcastChannel.postMessage({ type: 'LOGIN' })
}

const processSystemNotifications = notifications => {
  if (notifications && notifications.length) {
    if (notifications.length === 1) {
      systemNotify(notifications[0])
    } else {
      notifications.map(n =>
        showCommonToast(
          'Уведомление!', n.text, 'info',
          {
            'click-toast': () => store.dispatch('notification/confirmSeenNotification', n.id),
            'close-toast': () => store.dispatch('notification/confirmSeenNotification', n.id)
          }, {closeOnClick: true, timeout: false}
        )
      )
    }
  }
}

const processUnreadChatMessages = messages => {
  if (messages.length) {
    messages.map(m => showNewMessageToast(m.sender_full_name, m.text))
  }
}

export default {
  basicLogin({commit, getters, rootState, rootGetters}, params) {
    return new Promise((resolve, reject) => {
      axios.post('auth/token/', params, { basicAuth: true })
        .then(response => {
          const data = response.data
          const index = data.permissions
            ? data.permissions.findIndex(rule => rule === `view_${entityPatient}`)
            : null
          const useTwoFAAuth = params.useTwoFAAuth && data.two_f_a_auth_enabled
          const needChangePass = !!(
            data.password_state && (data.password_state.temp_password || data.password_state.need_refresh_password)
          )
          if (!needChangePass && !useTwoFAAuth && index > -1) {
            processLogin(commit, rootGetters, response)
          }
          resolve(response)
        })
        .catch(e => reject({ message: e }))
    })
  },
  logout({ commit }, reason) {
    const userData = getUserData()
    if (userData) {
      if ([
        appConstants.auth.logoutReason.END_OF_SESSION, appConstants.auth.logoutReason.INACTIVE_SESSION
      ].includes(reason)) {
        processLogout(commit, reason)
      } else {
        return new Promise(resolve => {
          axios({ url: `auth/user/${userData.id}/logout`, method: 'PATCH' })
            .then(() => {
              processLogout(commit, reason)
              resolve()
            })
            .catch(() => processLogout(commit, reason))
        })
      }
    } else if (router.currentRoute.name !== 'auth-login') {
      router.push({ name: 'auth-login' })
    }
  },
  logoutWithoutRequest: ({ commit }) => processLogout(commit, appConstants.auth.logoutReason.INVALID_TOKEN),
  // sms-auth
  sendSmsCode({commit}, params) {
    return new Promise((resolve, reject) => {
      axios.post('auth/send-sms-code/', null, { headers: { Authorization: `Bearer ${ params.token }` } })
        .then(response => resolve(response))
        .catch(e => reject({ message: e }))
    })
  },
  applySmsCode({commit, getters, rootState, rootGetters}, params) {
    return new Promise((resolve, reject) => {
      axios.post(
        'auth/apply-sms-code/', params.data, { headers: { Authorization: `Bearer ${ params.token }` } }
        )
        .then(response => {
          processLogin(commit, rootGetters, params.authResponse)
          resolve(response)
        })
        .catch(e => reject({ message: e }))
    })
  },
  // Change pass
  changePass({}, params) {
    return new Promise((resolve, reject) => {
      axios.patch(
        `auth/user/${params.userId}/change-pass`,
          params.data,
          { headers: { Authorization: `Bearer ${ params.token }` } }
        )
        .then(response => resolve(response))
        .catch(e => reject({ message: e }))
    })
  },
}
